import { Alert, Box, Button, Card, CardActions, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Delete, Edit, History as HistoryIcon, Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, extractError } from '../../../shared/api';
import type { Notice, NoticeAudience, NoticeVersion } from '../../../shared/types';
import { useAuthStore } from '../../../features/auth/store';
import { formatDateTime } from '../../../shared/utils';
import PageHeader from '../../../shared/PageHeader';

const AUDIENCES: NoticeAudience[] = ['ALL_PUBLIC', 'STUDENTS', 'TEACHERS', 'STAFF'];

export default function NoticeListPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = user?.role === 'ADMIN' || user?.role === 'PRINCIPAL';
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Notice | null>(null);
  const [creating, setCreating] = useState(false);
  const [historyId, setHistoryId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notices'],
    queryFn: async () => (await api.get<Notice[]>('/notices')).data,
  });

  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/notices/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notices'] }),
  });

  return (
    <Box>
      <PageHeader
        title="Notice Board"
        subtitle="Stay informed about announcements, schedules, and events."
        action={canManage && (
          <Button startIcon={<Add />} variant="contained" onClick={() => setCreating(true)}>New Notice</Button>
        )}
      />
      {isLoading && <Typography>Loading…</Typography>}
      <Stack spacing={2}>
        {(data ?? []).map((n) => (
          <Card key={n.id}>
            <CardContent>
              <Stack direction={{ sm: 'row' }} justifyContent="space-between" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Chip size="small" color="primary" label={n.audience.replace('_', ' ')} />
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(n.publishedAt)} · by {n.createdByName} · v{n.version}
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{n.title}</Typography>
                  <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{n.body}</Typography>
                </Box>
              </Stack>
            </CardContent>
            {canManage && (
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Tooltip title="Version history">
                  <IconButton size="small" onClick={() => setHistoryId(n.id)}><HistoryIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(n)}><Edit /></IconButton></Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => { if (window.confirm('Delete this notice?')) del.mutate(n.id); }}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </CardActions>
            )}
          </Card>
        ))}
        {!isLoading && (data?.length ?? 0) === 0 && (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Visibility sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography sx={{ mt: 2 }} color="text.secondary">No notices yet.</Typography>
          </Paper>
        )}
      </Stack>

      <NoticeForm
        open={creating || !!editing}
        notice={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
      />
      <VersionHistoryDialog noticeId={historyId} onClose={() => setHistoryId(null)} />
    </Box>
  );
}

function NoticeForm({ open, notice, onClose }: { open: boolean; notice: Notice | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(notice?.title ?? '');
  const [body, setBody] = useState(notice?.body ?? '');
  const [audience, setAudience] = useState<NoticeAudience>(notice?.audience ?? 'ALL_PUBLIC');

  const reset = () => { setTitle(notice?.title ?? ''); setBody(notice?.body ?? ''); setAudience(notice?.audience ?? 'ALL_PUBLIC'); };

  const save = useMutation({
    mutationFn: async () => {
      if (notice) {
        return (await api.put(`/notices/${notice.id}`, { title, body, audience })).data;
      }
      return (await api.post('/notices', { title, body, audience })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notices'] });
      reset();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" TransitionProps={{ onEntered: reset }}>
      <DialogTitle>{notice ? 'Edit Notice' : 'New Notice'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          <TextField label="Body" multiline minRows={5} value={body} onChange={(e) => setBody(e.target.value)} required />
          <TextField select label="Audience" value={audience} onChange={(e) => setAudience(e.target.value as NoticeAudience)}>
            {AUDIENCES.map((a) => <MenuItem key={a} value={a}>{a.replace('_', ' ')}</MenuItem>)}
          </TextField>
          {save.isError && <Alert severity="error">{extractError(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending || !title || !body}>
          {save.isPending ? 'Saving…' : notice ? 'Save changes' : 'Publish notice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function VersionHistoryDialog({ noticeId, onClose }: { noticeId: string | null; onClose: () => void }) {
  const { data } = useQuery({
    queryKey: ['notice-versions', noticeId],
    queryFn: async () => (await api.get<NoticeVersion[]>(`/notices/${noticeId}/versions`)).data,
    enabled: !!noticeId,
  });
  return (
    <Dialog open={!!noticeId} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Version history</DialogTitle>
      <DialogContent>
        {(!data || data.length === 0) ? (
          <Typography color="text.secondary">No prior versions.</Typography>
        ) : (
          <Stack spacing={2}>
            {data.map((v) => (
              <Card key={v.id} variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    v{v.version} · edited {formatDateTime(v.editedAt)} by {v.editedByName}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>{v.snapshot.title}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{v.snapshot.body}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
